export type LatLng = {
    latitude: number;
    longitude: number;
};

const EARTH_RADIUS_KM = 6373;
const EARTH_RADIUS_MI = 3960;

const toRad = (degree: number) => (degree * Math.PI) / 180;

export function distanceInKm(pointA: LatLng, pointB: LatLng): number {
    const deltaLatitude = toRad(pointA.latitude - pointB.latitude);
    const deltaLongitude = toRad(pointA.longitude - pointB.longitude);

    const a =
        Math.pow(Math.sin(deltaLatitude / 2), 2) +
        Math.pow(Math.sin(deltaLongitude / 2), 2) *
            Math.cos(toRad(pointA.latitude)) *
            Math.cos(toRad(pointB.latitude));

    const hav = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return hav * EARTH_RADIUS_KM;
}

export function humanizeDistance(
    pointA: LatLng,
    pointB: LatLng,
    locale = "en-US",
    unitSystem = "metric"
) {
    const distanceKm = distanceInKm(pointA, pointB);

    const result =
        unitSystem === "metric"
            ? {
                  distance: distanceKm,
                  unit: "km",
                  smallUnit: "m",
                  factor: 1000,
                  smallBorder: 0.9,
                  largeBorder: 200,
              }
            : {
                  distance: (distanceKm / EARTH_RADIUS_KM) * EARTH_RADIUS_MI,
                  unit: "mi",
                  smallUnit: "yd",
                  factor: 1760,
                  smallBorder: 0.5,
                  largeBorder: 300,
              };

    const formatter = new Intl.NumberFormat(locale, { maximumSignificantDigits: 2 });
    if (result.distance < result.smallBorder) {
        let distance = result.distance * result.factor;
        if (distance < 40) return `< 50 ${result.smallUnit}`;

        distance = Math.round(distance / 50) * 50;
        return `${formatter.format(distance)} ${result.smallUnit}`;
    } else if (result.distance > result.largeBorder) {
        return `>${formatter.format(result.largeBorder)} ${result.unit}`;
    }

    return `${formatter.format(result.distance)} ${result.unit}`;
}
